import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { API_CONFIG } from '../config/api';
import { MessageSquare, User, Mail, Phone, X } from 'lucide-react';

const ProjectQuestionForm = ({ project, onClose, formTitle, projectLabel, showQuestion = true }) => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '+90 ', question: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace('+90 ', '').replace(/\D/g, '');
    const limited = phoneNumber.slice(0, 10);
    
    let formatted = '+90 ';
    if (limited.length > 0) formatted += limited.slice(0, 3);
    if (limited.length > 3) formatted += ' ' + limited.slice(3, 6);
    if (limited.length > 6) formatted += ' ' + limited.slice(6, 8);
    if (limited.length > 8) formatted += ' ' + limited.slice(8, 10);
    
    return formatted;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setForm({...form, phone: formatted});
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation: Always require name and email
    if (!form.full_name || !form.email) {
      setError('Ad, e‑posta girin');
      return;
    }
    
    // Validation: If showQuestion is true, question is required
    if (showQuestion && !form.question.trim()) {
      setError('Ad, e‑posta ve sorunuzu girin');
      return;
    }
    
    setSending(true);
    try {
      let payload;
      if (project?.id && project?.name) {
        payload = {
          project_id: project.id,
          project_name: project.name,
          customer_name: form.full_name,
          customer_email: form.email,
          customer_phone: form.phone || null,
          company_name: null,
          project_description: showQuestion ? form.question.trim() : `${project.name} hakkında talep`,
        };
      } else {
        payload = {
          service_id: 'project_question',
          service_title: showQuestion 
            ? `Proje Sorusu: ${project?.name || ''} (${project?.id || ''})`
            : `Demo Talebi: ${project?.name || ''} (${project?.id || ''})`,
          customer_name: form.full_name,
          customer_email: form.email,
          customer_phone: form.phone || null,
          company_name: null,
          project_description: showQuestion 
            ? form.question.trim() 
            : `${project?.name || 'Yapay zeka'} demo talebi`,
          additional_requirements: JSON.stringify({ project_id: project?.id }),
        };
      }
      const res = await fetch(`${API_CONFIG.BASE_URL}/backend/api.php?action=createQuoteRequest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Gönderilemedi');
      setSent(true);
      setTimeout(() => onClose && onClose(), 1200);
    } catch (e) {
      setError('Gönderim başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <Card className="w-full bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <CardContent className="p-10 text-center">
          <p className="text-2xl font-bold text-green-800">Sorunuz alındı!</p>
          <p className="text-green-700 mt-2">Kısa süre içinde dönüş yapacağız.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full shadow-xl relative border-0">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg relative">
        <CardTitle className="text-2xl flex items-center gap-2">
          <MessageSquare className="w-5 h-5" /> {formTitle || 'Proje Hakkında Soru Sor'}
        </CardTitle>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white w-9 h-9 rounded-full"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-6">
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="full_name" className="flex items-center gap-2"><User className="w-4 h-4"/> Ad Soyad *</Label>
              <Input id="full_name" value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} required />
            </div>
            <div>
              <Label htmlFor="email" className="flex items-center gap-2"><Mail className="w-4 h-4"/> E‑posta *</Label>
              <Input 
                id="email" 
                type="email" 
                placeholder="ornek@email.com"
                value={form.email} 
                onChange={e=>setForm({...form, email:e.target.value})} 
                pattern="[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}"
                title="Geçerli bir e-posta adresi girin"
                required 
              />
            </div>
            <div>
              <Label htmlFor="phone" className="flex items-center gap-2"><Phone className="w-4 h-4"/> Telefon</Label>
              <Input 
                id="phone" 
                type="tel"
                placeholder="+90 555 123 45 67"
                value={form.phone} 
                onChange={handlePhoneChange}
                title="Telefon numaranızı girin"
              />
            </div>
            <div>
              <Label>{projectLabel || 'Proje'}</Label>
              <Input value={`${project?.name || ''}`} disabled />
            </div>
          </div>
          {showQuestion && (
            <div>
              <Label htmlFor="question" className="flex items-center gap-2"><MessageSquare className="w-4 h-4"/> Sorunuz *</Label>
              <textarea id="question" rows={5} value={form.question} onChange={e=>setForm({...form, question:e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500" required />
            </div>
          )}
          {error && <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Vazgeç</Button>
            <Button type="submit" disabled={sending} className={`${sending? 'bg-purple-300':'bg-purple-600 hover:bg-purple-700'} text-white`}>
              {sending ? 'Gönderiliyor...' : 'Gönder'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ProjectQuestionForm;






